import { defineComponent, nextTick } from 'vue';
import { QBtn } from 'quasar';
import AceEditor from '../../../3rdParty/AceEditor.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';

const BTN = `${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`;

/**
 * Popup cell editor for GlobalVar Type=object (Data / advanced).
 * Enter inserts a newline inside Ace; Ctrl+Enter or Apply commits.
 */
export default defineComponent({
    name: 'GlobalVarObjectEditor',
    components: { AceEditor, QBtn },
    props: {
        params: { type: Object, default: null },
    },
    data() {
        return {
            text: '',
            localError: null,
        };
    },
    created() {
        const initial = this.params?.value;
        this.text = initial == null ? '' : String(initial);
    },
    methods: {
        getValue() {
            const ace = this.$refs.ace;
            if (ace?.getContent)
                return ace.getContent();
            return this.text;
        },
        isPopup() {
            return true;
        },
        getPopupPosition() {
            return 'under';
        },
        afterGuiAttached() {
            const focus = () => {
                const editor = this.$refs.ace?.editor;
                if (!editor)
                    return false;
                editor.resize?.();
                editor.focus?.();
                editor.container?.addEventListener?.('keydown', this.onAceKeydown, false);
                return true;
            };
            nextTick(() => {
                if (!focus())
                    setTimeout(focus, 50);
            });
        },
        onAceKeydown(e) {
            if (!e)
                return;
            const key = e.key;
            const commit = (e.ctrlKey || e.metaKey) && key === 'Enter';
            if (commit) {
                e.preventDefault();
                e.stopPropagation();
                this.apply();
                return;
            }
            // Keep Enter inside Ace (newline). Do not let ag-grid commit the cell.
            if (key === 'Enter')
                e.stopPropagation();
        },
        apply() {
            const value = this.getValue();
            if (value != null && String(value).trim() !== '') {
                try {
                    JSON.parse(value);
                }
                catch (err) {
                    this.localError = err?.message || 'Invalid JSON';
                    return;
                }
            }
            this.localError = null;
            this.params?.api?.stopEditing?.();
        },
        cancel() {
            this.params?.api?.stopEditing?.(true);
        },
    },
    beforeUnmount() {
        this.$refs.ace?.editor?.container?.removeEventListener?.('keydown', this.onAceKeydown, false);
    },
    render() {
        return (
            <div
                class="global-var-object-editor"
                style={{
                    width: '520px',
                    height: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'white',
                    border: '1px solid #cfd8dc',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                    overflow: 'hidden',
                }}
                onKeydown={this.onAceKeydown}
            >
                <div style={{ flex: 1, minHeight: 0 }}>
                    <AceEditor
                        ref="ace"
                        lang="json"
                        theme="github"
                        fontSize={13}
                        wordWrap={true}
                        content={this.text}
                        onChange-content={(v) => { this.text = v == null ? '' : String(v); }}
                    />
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 8px',
                    borderTop: '1px solid #eceff1',
                    background: '#fafafa',
                }}>
                    <span class={this.localError ? 'text-caption text-negative' : 'text-caption text-grey-7'} style={{ flex: 1 }}>
                        {this.localError || 'Enter adds a line. Ctrl+Enter applies.'}
                    </span>
                    <QBtn
                        flat
                        dense
                        no-caps
                        class={BTN}
                        label="Cancel"
                        onClick={() => this.cancel()}
                    />
                    <QBtn
                        unelevated
                        dense
                        no-caps
                        color="primary"
                        class={BTN}
                        label="Apply"
                        onClick={() => this.apply()}
                    />
                </div>
            </div>
        );
    },
});
